import { AxiosError } from 'axios'
import { FormikErrors, useFormik } from 'formik'
import { useCallback, useEffect, useState } from 'react'

import { ButtonType, formatDateTime, OptionsHelper } from '../../../../../../_metronic/helpers'
import {
  declararInterrupcao,
  getInterrupcoesAtendimento,
} from '../../../atendimento/core/_requests'
import { InterrupcaoAtendimentoView } from '../../../atendimento/core/_models'
import {
  Button,
  FormDateTimeInput,
  FormRadio,
  FormSelect,
  FormTextArea,
  IOption,
  IRadioOption,
  LoadingIndicator,
  useModal,
} from '../../../../../components'
import {
  CAUSA_INTERRUPCAO_OUTRA,
  interrupcaoAtendimentoSchema,
} from './validation/interrupcaoAtendimentoSchema'

type Props = {
  atendimentoId: number
}

type FormValues = {
  dhInicio: string
  dhFim: string
  retomada: boolean
  quemCaiu: IOption<string> | null
  causa: IOption<string> | null
  causaDetalhe: string
}

const OPCOES_RETOMADA: IRadioOption<boolean>[] = [
  { id: 'sim', descricao: 'Sim, a consulta continuou', value: true },
  { id: 'nao', descricao: 'Não, a consulta não continuou', value: false },
]

/** Valor inicial do `datetime-local`: agora, em horário local, sem segundos. */
const agoraLocal = (): string => {
  const agora = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${agora.getFullYear()}-${pad(agora.getMonth() + 1)}-${pad(agora.getDate())}T${pad(
    agora.getHours()
  )}:${pad(agora.getMinutes())}`
}

const valoresIniciais = (): FormValues => ({
  dhInicio: agoraLocal(),
  dhFim: '',
  retomada: true,
  quemCaiu: null,
  causa: null,
  causaDetalhe: '',
})

/**
 * Declaração de interrupção da teleconsulta.
 *
 * Vive fora do caminho de encerramento de propósito: o registro é opcional e,
 * se o POST falhar, o profissional continua podendo finalizar o atendimento
 * normalmente. Por isso o erro aqui é sempre um aviso não bloqueante dentro do
 * próprio modal, com nova tentativa manual — nunca um alerta global nem
 * retentativa automática (que duplicaria a linha e inflaria o indicador, já que
 * a tabela é append-only e não tem chave de deduplicação).
 */
export const InterrupcaoAtendimentoModal: React.FC<Props> = ({ atendimentoId }) => {
  const { closeModal } = useModal()

  const [declaradas, setDeclaradas] = useState<InterrupcaoAtendimentoView[]>([])
  const [aviso, setAviso] = useState<string>('')
  const [sucesso, setSucesso] = useState<string>('')

  const carregarDeclaradas = useCallback(async () => {
    try {
      setDeclaradas(await getInterrupcoesAtendimento(atendimentoId))
    } catch {
      setAviso('Não foi possível carregar as interrupções já declaradas deste atendimento.')
    }
  }, [atendimentoId])

  useEffect(() => {
    carregarDeclaradas()
  }, [carregarDeclaradas])

  const formik = useFormik<FormValues>({
    initialValues: valoresIniciais(),
    validationSchema: interrupcaoAtendimentoSchema,
    onSubmit: async (values, { setSubmitting, setErrors, setTouched, resetForm }) => {
      setSucesso('')
      setAviso('')
      setSubmitting(true)
      try {
        const causa = values.causa?.value as string
        const registrada = await declararInterrupcao(atendimentoId, {
          dhInicio: values.dhInicio,
          dhFim: values.retomada ? values.dhFim : null,
          causa,
          causaDetalhe: causa === CAUSA_INTERRUPCAO_OUTRA ? values.causaDetalhe.trim() : null,
          quemCaiu: values.quemCaiu?.value as string,
          retomada: values.retomada,
        })
        setDeclaradas((anteriores) => [...anteriores, registrada])
        resetForm({ values: valoresIniciais() })
        setSucesso('Interrupção declarada com sucesso.')
      } catch (error) {
        const err = error as AxiosError<{ data?: { errors?: Record<string, string[]> } }>
        const errosDoServidor = err.response?.data?.data?.errors
        if (errosDoServidor) {
          const campos = Object.keys(errosDoServidor)
          setErrors(
            Object.fromEntries(
              campos.map((campo) => [campo, errosDoServidor[campo]?.[0]])
            ) as FormikErrors<FormValues>
          )
          setTouched(Object.fromEntries(campos.map((campo) => [campo, true])), false)
        }
        setAviso(
          'Não foi possível salvar a declaração agora. O encerramento do atendimento não é afetado — você pode tentar novamente.'
        )
      } finally {
        setSubmitting(false)
      }
    },
  })

  const { retomada, dhInicio, causa } = formik.values

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <p className='text-gray-700 fs-7'>
        O registro é opcional e independente do encerramento: se falhar, você continua podendo
        finalizar o atendimento normalmente. Pode declarar mais de uma interrupção.
      </p>

      <div aria-live='polite'>
        {aviso && (
          <div
            className='alert alert-warning text-gray-900 d-flex align-items-center'
            role='status'
          >
            <span className='fw-bold me-2'>Atenção:</span>
            <span>{aviso}</span>
          </div>
        )}
        {sucesso && (
          <div className='alert alert-success' role='status'>
            {sucesso}
          </div>
        )}
      </div>

      {declaradas.length > 0 && (
        <div className='mb-5'>
          <h3 className='fs-6 fw-bold'>Interrupções já declaradas</h3>
          <ul className='list-unstyled mb-0'>
            {declaradas.map((item) => (
              <li key={item.id} className='border-bottom py-2 fs-7'>
                <span className='fw-bold'>{formatDateTime(item.dhInicio)}</span>
                {' — '}
                <span>{item.causaDescricao ?? item.causa}</span>
                {', '}
                <span>{item.quemCaiuDescricao ?? item.quemCaiu}</span>
                {', '}
                <span>{item.retomada ? 'consulta retomada' : 'consulta não retomada'}</span>
                {item.idInterrupcaoRetificada ? (
                  <span className='text-gray-700'>
                    {' '}
                    (retifica o registro {item.idInterrupcaoRetificada})
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className='row g-4'>
        <FormDateTimeInput
          formik={formik}
          name='dhInicio'
          label='Início da interrupção'
          placeholder='Informe o início da interrupção'
          className='col-md-6'
          max={new Date()}
          isRequired
        />

        <FormRadio<boolean>
          formik={formik}
          name='retomada'
          label='A consulta foi retomada?'
          options={OPCOES_RETOMADA}
          className='col-md-6'
          isColumn
        />

        {retomada && (
          <FormDateTimeInput
            formik={formik}
            name='dhFim'
            label='Fim da interrupção'
            placeholder='Informe o fim da interrupção'
            className='col-md-6'
            min={dhInicio ? new Date(dhInicio) : undefined}
            isRequired
          />
        )}

        <FormSelect
          formik={formik}
          name='quemCaiu'
          label='Quem perdeu a conexão'
          placeholder='Selecione'
          fetchOptions={OptionsHelper.getOptionsQuemCaiuInterrupcao}
          className='col-md-6'
          isDisabled={formik.isSubmitting}
          isRequired
        />

        <FormSelect
          formik={formik}
          name='causa'
          label='Causa da interrupção'
          placeholder='Selecione'
          fetchOptions={OptionsHelper.getOptionsCausasInterrupcao}
          className='col-md-6'
          isDisabled={formik.isSubmitting}
          isRequired
        />

        {causa?.value === CAUSA_INTERRUPCAO_OUTRA && (
          <div className='col-12'>
            <FormTextArea
              formik={formik}
              name='causaDetalhe'
              label='Descreva a causa'
              rows={2}
              isRequired
            />
            <div className='form-text'>
              Descreva apenas a falha técnica. Não inclua dados do paciente neste campo.
            </div>
          </div>
        )}
      </div>

      <div className='d-flex justify-content-end mt-6 gap-3'>
        <Button
          type={ButtonType.Reset}
          className='btn-secondary'
          onClick={closeModal}
          disabled={formik.isSubmitting}
        >
          Fechar
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : 'Declarar interrupção'}
        </Button>
      </div>
    </form>
  )
}
